import React, {useCallback, useState, useEffect, useMemo, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {frameCommunicationEvents, useFrameService, useFrameKey} from '@ey/canvascoreservices';
import {frameSubEvent, trialBalanceCategories} from '../../../../../../util/uiconstants';
import Utility from '../../../../../../util/utilityfunctions';
import {labels, getFrameEssentialQuery} from '../../../../../../util/utils';
import {
	useDocumentSwitcher,
	useEngagement,
	useEngagementId,
	useQuery,
	useRefresh,
	useTaskId
} from '../../../../../../util/customHooks';
import {
	useAresUrlParameters,
	useIsDrawerMode,
	useIsTrackChangesView,
	useIsCompareResponsesView,
	useAresUrlCompareResponsesParameters,
	useAresUrlTrackChangesParameters,
	expandableGetFormById,
	useNavigationFilter,
	useSelectedStEntityId
} from '../../../../../../util/Ares/customHooks';
import NeonSkinContainer from '../../../NeonSkinContainer';
import OverrideLabelsProvider from '../../../../Forms/Utilities/OverrideLabelsProvider';
import TaskViewDetails from '../../TaskViewDetails';
import FormDetailsV2 from './FormDetailsV2';
import NavigationPanelSwitcher from './NavigationPanel/NavigationPanelSwitcher';
import {getNavigationData} from '../Common/guidedWorkflow.utils';
import GrayFade from '../../../../../Common/GrayFade/GrayFade';
import {loadStorage} from '../../../../../../components/Common/localStorage';
import {setIsCompareResponsesView, setIsTrackChangesView} from '../../../../../../actions/Engagement/engagementactions';
import {clearSnapshotCompareView, getSnapshotById} from '../../../../../../actions/Snapshot/snapshotdetailsactions';
import {GetHelixProjectFromCanvas} from '../../../../../../actions/HelixIntegration/engagementhelixaction';
import {setDocumentId} from '../../../../../../actions/Document/documentactions';
import {raiseJSValidationError} from '../../../../../../actions/erroractions';
import {clearEntityDocuments} from '../../../../../../actions/Document/entityDocumentActions';
import {
	setSelectedStEntityId,
	clearSelectedStEntityId
} from '../../../../../../actions/MultiEntities/stEntitiesactions';
import {getTaskDocumentByDocumentId} from '../../../../../../selectors/Ares/selectors';
import LoadingIndicator from '../../../../../Common/LoadingIndicator/LoadingIndicator';
import ReactiveTabRenamer from '../../../../../Common/Modal/ReactiveTabRenamer';
import {getBannerConfig} from './Banner/bannerConfig';

export default function GuidedWorkflowV2Container(props) {
	const {entityDetails} = props;

	const dispatch = useDispatch();

	const {taskId, documentId, headerId, sectionId, stEntityId: urlStEntityId, isSummary} = useAresUrlParameters();

	const engagementId = useEngagementId();

	const engagement = useEngagement();

	const isDrawerMode = useIsDrawerMode();

	const isTrackChangesView = useIsTrackChangesView();

	const isCompareResponsesView = useIsCompareResponsesView();

	const navFilter = useNavigationFilter();

	const currentDocument = useSelector((store) => getTaskDocumentByDocumentId(store, documentId));

	const documentFirstTaskId = useTaskId(documentId);
	const [currentStEntityId] = useSelectedStEntityId();

	const selectedSnapshotId = loadStorage('selectedTrackChangesSnapshotId');

	let query = useQuery();

	const navigate = useNavigate();

	const frameService = useFrameService();

	const documentSwitcher = useDocumentSwitcher({includeReviewNotes: true, includeApprovals: true});

	const {isCompareResponses, isBodyDetailView} = useAresUrlCompareResponsesParameters();

	const {isTrackChanges} = useAresUrlTrackChangesParameters();
	const frameKey = useFrameKey();
	useRefresh();

	const isFrame = useMemo(() => window.self !== window.top, []);
	const storeDocumentId = useSelector((store) => store.documentId);

	const navigateToUrl = useCallback(
		async (url) => {
			return dispatch(clearSelectedStEntityId()).then(() => navigate(url));
		},
		[dispatch]
	);

	const {
		firstHeaderId,
		firstSectionId,
		documentTypeId,
		firstTaskId,
		forceDefaultView,
		isIndependenceOrProfileForm
	} = useMemo(() => {
		const _documentTypeId = currentDocument?.documentTypeId;
		let isIndependenceOrProfileForm;
		if (_documentTypeId) {
			isIndependenceOrProfileForm = Utility.isDocumentIndependenceOrProfileForm(_documentTypeId);
		}

		return {
			firstHeaderId: currentDocument?.firstHeaderId,
			firstSectionId: currentDocument?.firstSectionId,
			documentTypeId: _documentTypeId,
			firstTaskId: documentFirstTaskId,
			forceDefaultView: query?.forcedefaultview,
			isIndependenceOrProfileForm
		};
	}, [currentDocument, query]);

	// This is to redirect to the default url for the activity (form's first header and section) when the url doesn't have header and section ids.
	useEffect(() => {
		if (storeDocumentId !== documentId) {
			dispatch(setDocumentId(documentId)).catch(() => {
				dispatch(raiseJSValidationError(labels.invalidDocumentId));
			});
			dispatch(clearEntityDocuments());
		}
		if (firstHeaderId && firstSectionId && (!headerId || !sectionId) && (!isSummary || forceDefaultView)) {
			const defaultUrl = Utility.getAresTaskListNavigationUrl(
				documentId,
				firstHeaderId,
				firstSectionId,
				engagementId,
				firstTaskId,
				query,
				documentTypeId,
				isTrackChangesView,
				isCompareResponsesView,
				false,
				currentStEntityId
			);

			navigateToUrl(defaultUrl).then((r) => r);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentId, firstHeaderId, headerId, sectionId, isSummary, navigateToUrl]);

	// Form details content render
	const contentRender = useMemo(() => {
		return (
			<FormDetailsV2
				taskId={taskId}
				documentId={documentId}
				headerId={headerId}
				sectionId={sectionId}
				isDrawerMode={isDrawerMode}
				isTrackChangesView={isTrackChangesView}
				isCompareResponsesView={isCompareResponsesView}
				isBodyDetailView={isBodyDetailView}
				entityDetails={entityDetails}
			/>
		);
	}, [
		taskId,
		documentId,
		headerId,
		sectionId,
		isDrawerMode,
		isTrackChangesView,
		isCompareResponsesView,
		isBodyDetailView,
		entityDetails
	]);

	const [isLoading, setIsLoading] = useState(false);

	const getFirstActivity = (response) => {
		const firstActivity = response?.tasks?.sort(Utility.sortByMultipleKey('knowledgeTaskDisplayOrder'))[0];

		// First document related to the activity (task) found in the response
		const relatedDocument = response?.documents.find((doc) => doc.taskId === firstActivity?.id);

		// Build document url and redirect:
		if (relatedDocument) {
			return Utility.getAresTaskListNavigationUrl(
				relatedDocument.id,
				undefined,
				undefined,
				engagementId,
				relatedDocument.taskId,
				query,
				relatedDocument.documentTypeId,
				isTrackChangesView,
				isCompareResponsesView,
				false,
				currentStEntityId
			);
		}
	};

	// Finds the first header for the given documentId
	const getFirstHeader = (documentId, response) => {
		const formHeaders = response.headers.filter((h) => h.documentId === documentId);

		const firstHeader = formHeaders?.sort(Utility.sortByMultipleKey('knowledgeDisplayOrder'))[0];

		// To avoid mutating the response object
		return {...firstHeader};
	};

	// Finds the first section for the given documentId and headerId
	const getFirstSection = (documentId, headerId, response) => {
		const formSections = response.sections.filter((s) => s.documentId === documentId && s.formHeaderId === headerId && s.isDisplayed);

		let firstSection =
			formSections?.length > 0 && formSections?.sort(Utility.sortByMultipleKey('knowledgeDisplayOrder'))[0];

		return {...firstSection};
	};


	// TaskOverview call - entity selection
	const getTaskOverViewForEntity = () => {
		if (currentStEntityId) {
			setIsLoading(true);
			getNavigationData(
				engagementId,
				currentDocument,
				undefined,
				dispatch,
				engagement?.isMultiEntityEngagement,
				currentStEntityId,
				navFilter,
				false,
				false
			)
				.then((responses) => {
					if (engagement?.isMultiEntityEngagement) {
						let url = undefined;
						const taskOverviewResponse = responses?.taskOverview;
						let _taskId = undefined,
							_document = undefined,
							_header = undefined,
							_section = undefined;
						const filteredDocuments = taskOverviewResponse?.documents.filter(
							(d) => d.knowledgeFormId === currentDocument.knowledgeFormId
						);

						/*see if there is a doc with same knowledgeFormId in responses*/
						_document = filteredDocuments?.find((d) => d.id === currentDocument.id) || filteredDocuments[0];

						if (_document) {
							let currentSectionId = sectionId;
							let currentHeaderId = headerId;
							_taskId = _document.taskId;
							const pathnameSegments = window.location.pathname.split('/');
							currentHeaderId = Number(pathnameSegments[4] || 0);
							currentSectionId = Number(pathnameSegments[6] || 0);
							/*get header and section id from current document that matches the header and section in the url*/
							_header = taskOverviewResponse?.headers.find(
								(header) =>
									header.documentId === _document.id &&
									header.knowledgeFormHeaderId ===
										currentDocument.headers?.find((h) => h.id === currentHeaderId)?.knowledgeFormHeaderId
							);
							const currentDocumentSection = Utility.findSectionById(currentDocument.headers, Number(currentSectionId));
							if (currentDocumentSection) {
								_section = taskOverviewResponse?.sections.find(
									(section) =>
										section.documentId === _document.id &&
										section.knowledgeFormSectionId === currentDocumentSection.knowledgeFormSectionId
								);
							}

							url = getUrl(
								_document,
								_header,
								_section,
								_taskId,
								engagementId,
								query,
								currentDocument,
								isTrackChangesView,
								isCompareResponsesView,
								currentStEntityId,
								taskOverviewResponse
							);
						} else {
							url = getFirstActivity(taskOverviewResponse);
						}
						if (url) {
							navigateToUrl(url);
						}
					}
				})
				.finally(() => setIsLoading(false));
		}
	};

	const getUrl = (
		_document,
		_header,
		_section,
		_taskId,
		engagementId,
		query,
		currentDocument,
		isTrackChangesView,
		isCompareResponsesView,
		currentStEntityId,
		taskOverviewResponse
	) => {
		let url = undefined;
		let firstFormHeader = getFirstHeader(_document?.id, taskOverviewResponse);
		if (_document && _header && _section?.isDisplayed) {
			url = Utility.getAresTaskListNavigationUrl(
				_document.id,
				_header?.id,
				_section?.id,
				engagementId,
				_taskId,
				query,
				currentDocument.documentTypeId,
				isTrackChangesView,
				isCompareResponsesView,
				false,
				currentStEntityId
			);
		} else if (_document) {
			if (_header) {
				// _section = _document.headers?.find((h) => h.id === _header.id)?.sections?.[0];
				_section = getFirstSection(_document.id, _header.id, taskOverviewResponse);
			} else {
				_section = null;
			}

			if (_section?.isDisplayed) {
				url = Utility.getAresTaskListNavigationUrl(
					_document.id,
					_header.id,
					_section.id,
					engagementId,
					_taskId,
					query,
					currentDocument.documentTypeId,
					isTrackChangesView,
					isCompareResponsesView,
					false,
					currentStEntityId
				);
			} else if (firstFormHeader) {
				// _header = _document.headers[0];
				// _section = _header.sections[0];

				_header = firstFormHeader;
				_section = getFirstSection(_document.id, _header.id, taskOverviewResponse);

				if (_section?.isDisplayed) {
					url = Utility.getAresTaskListNavigationUrl(
						_document.id,
						_header.id,
						_section.id,
						engagementId,
						_taskId,
						query,
						currentDocument.documentTypeId,
						isTrackChangesView,
						isCompareResponsesView,
						false,
						currentStEntityId
					);
				} else {
					url = getFirstActivity(taskOverviewResponse);
				}
			} else {
				url = getFirstActivity(taskOverviewResponse);
			}
		} else {
			url = getFirstActivity(taskOverviewResponse);
		}
		return url;
	};

	const handleOnEntitySelected = useCallback((selectedStEntityId) => {
		return dispatch(setSelectedStEntityId(selectedStEntityId));
	}, []);

	const getSnapshotDetails = useCallback(
		(snapshotId, sourceSnapshotId) => {
			dispatch(clearSnapshotCompareView(true));
			if (engagement.isProfileComplete) {
				dispatch(getSnapshotById(engagementId, documentId, snapshotId, sourceSnapshotId));
			}
		},
		[dispatch, engagementId, documentId]
	);

	// TaskOverview call - Page load
	useEffect(() => {
		if (
			engagement?.isMultiEntityEngagement !== undefined &&
			currentDocument?.id &&
			currentDocument?.documentChangeTypeId !== undefined &&
			currentDocument?.smartEvidenceSourceId !== undefined
		) {
			//We are the validation call inside left nav now.
			let includeValidations = false;
			let includeDocsGetAll = false;

			if (!currentStEntityId) {
				getNavigationData(
					engagementId,
					currentDocument,
					undefined,
					dispatch,
					engagement?.isMultiEntityEngagement,
					null,
					navFilter,
					includeValidations,
					includeDocsGetAll
				).finally(() => setIsLoading(false));
			} else {
				!isBodyDetailView && getTaskOverViewForEntity();
			}
		}
	}, [
		engagement?.isMultiEntityEngagement,
		currentDocument?.id,
		currentDocument?.documentChangeTypeId,
		currentDocument?.smartEvidenceSourceId,
		currentStEntityId,
		navFilter
	]);

	useEffect(() => {
		if (!isCompareResponsesView) {
			dispatch(setIsCompareResponsesView(isCompareResponses));
		}
	}, [dispatch, isCompareResponses, isCompareResponsesView]);

	useEffect(() => {
		if (!isTrackChangesView) {
			dispatch(setIsTrackChangesView(isTrackChanges));
		}
	}, [dispatch, isTrackChanges, isTrackChangesView]);

	useEffect(() => {
		if (selectedSnapshotId > 0) {
			getSnapshotDetails(selectedSnapshotId, -1);
		}
	}, [selectedSnapshotId]);

	useEffect(() => {
		// Intentionally comparing with false here..
		if (engagementId && isIndependenceOrProfileForm === false) {
			const query = {
				trialBalanceTypeCategory: trialBalanceCategories.Planning,
				IncludeConnections: true
			};

			if (currentStEntityId) {
				query.multiEntityIds = currentStEntityId;
			}

			dispatch(GetHelixProjectFromCanvas(engagementId, query));
		}
	}, [engagementId, isIndependenceOrProfileForm, currentStEntityId, dispatch]);

	useEffect(() => {
		if (engagement && engagement.engagementId && engagementId && documentId) {
			dispatch(expandableGetFormById(engagement, documentId, undefined, undefined, undefined, true));
		}
	}, [documentId]);

	const subscriptionRef = useRef(null);

	useEffect(() => {
		if (isFrame) {
			subscriptionRef.current = frameService.subscribeFrame({
				next: (args) => {
					switch (args.event) {
						case frameCommunicationEvents.MESSAGE: {
							if (args.subEvent === frameSubEvent.COMPARE_SECTION_CHANGED) {
								const url = Utility.getAresTaskListNavigationUrl(
									args.documentId,
									args.headerId,
									args.sectionId,
									args.engagementId,
									args.taskId,
									getFrameEssentialQuery(args.queryParameters),
									args.documentTypeId,
									isTrackChangesView,
									isCompareResponsesView,
									false,
									currentStEntityId
								);

								navigateToUrl(url);

								if (documentId !== args.documentId) {
									documentSwitcher.switchDocument(args.documentId);
								}
							} else if (args.subEvent === frameSubEvent.TRACK_CHANGES_SECTION_CHANGED) {
								const queryParameters = getFrameEssentialQuery({
									isDrawer: true,
									isTrackChanges: true,
									snapshotId: selectedSnapshotId,
									target: args.target
								});

								const url = Utility.getAresTaskListNavigationUrl(
									args.documentId,
									args.headerId,
									args.sectionId,
									args.engagementId,
									args.taskId,
									queryParameters,
									args.documentTypeId,
									false,
									isCompareResponsesView,
									false,
									currentStEntityId
								);

								navigateToUrl(url);
							}
							break;
						}
					}
				}
			});
		}

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSnapshotId, isFrame, documentId, documentSwitcher]);

	const showLeftNavigation = Utility.documentHasLeftNavigation(currentDocument?.documentTypeId);

	return (
		<ReactiveTabRenamer>
			<NeonSkinContainer enabled>
				<OverrideLabelsProvider labelOverrideCode={'Ares'}>
					<StyledGuidedWorkflowV2Container
						className={`StyledGuidedWorkflowV2Container StyledTaskViewWrapper StyledTaskViewContainer
									${isDrawerMode ? 'drawer' : ''}
									${isLoading ? 'LoaderVisible' : ''}`}
					>
						{currentDocument && getBannerConfig(currentDocument.documentTypeId, {document: currentDocument})}
						{!isBodyDetailView && headerId && sectionId && showLeftNavigation && (
							<GrayFade isGrayFade={isLoading} className="navigation-panel-gray-fade">
								<NavigationPanelSwitcher
									engagementId={engagementId}
									taskId={taskId}
									documentId={documentId}
									headerId={headerId}
									sectionId={sectionId}
									onEntitySelected={handleOnEntitySelected}
								/>
							</GrayFade>
						)}
						{headerId && sectionId && (
							<TaskViewDetails
								engagementId={engagementId}
								taskId={taskId}
								documentId={documentId}
								headerId={headerId}
								sectionId={sectionId}
								showBreadcrumbs={false}
								showTopNav={false}
								contentRender={contentRender}
								isBodyDetailView={isBodyDetailView}
							/>
						)}
						{!headerId ? (
							!currentDocument ? (
								<LoadingIndicator show view="blockview" styleName="LoadingIndicator" />
							) : (
								currentDocument &&
								(!currentDocument?.headers || currentDocument?.headers.length === 0 ? (
									<span className="no-response">{labels.noResponseAvailable}</span>
								) : (
									!currentDocument.headers.some((header) => header.sections && header.sections.length > 0) && (
										<span className="no-response">{labels.noContentAvailable}</span>
									)
								))
							)
						) : (
							<> </>
						)}
					</StyledGuidedWorkflowV2Container>
				</OverrideLabelsProvider>
			</NeonSkinContainer>
		</ReactiveTabRenamer>
	);
}

const StyledGuidedWorkflowV2Container = styled.section`
	font-size: var(--px-12);
	display: flex;
	height: 100%;
	overflow: hidden;
	background-color: transparent;
	flex-direction: column;
	.background-wrap1 {
		position: absolute;
		top: var(--px-55);
		left: 0;
		width: 100%;
		height: 100%;
		background-color: var(--neutrals-700);
		opacity: 0.04;
		clip-path: polygon(0% 0%, 0% var(--px-180), 79vw 0%);
	}
	.background-wrap2 {
		position: absolute;
		top: var(--px-55);
		left: 0;
		width: 100%;
		height: 100%;
		background-color: var(--neutrals-700);
		opacity: 0.06;
		clip-path: polygon(100% 0%, 60vw 0%, 100% var(--px-80));
	}
	.no-response {
		display: flex;
		justify-content: center;
		align-items: center;
		height: 100%;
		font-size: var(--px-14);
		font-weight: 300;
	}
`;
